import { Texture } from "@/core/backends/webgl/texture"
import { mat3, vec2 } from "gl-matrix"
import { RawDemosaicer, RawDemosaicerOptions } from "./raw-demosaicer"
import { RawInfo } from "@/lib/raw/fastlib"
import { Renderer } from "@/core/pipeline/renderer"
import { DualBuffers } from "@/core/pipeline/dual-buffers"

// Reference src:
// https://github.com/RawTherapee/RawTherapee/blob/dev/rtengine/xtrans_demosaic.cc
// https://gist.github.com/TJC/09920b086911ad3a24a4

interface FujiMultipliersPattern {
	r: mat3
	g: mat3
	b: mat3
}

interface FujiMultipliers {
	a: FujiMultipliersPattern
	b: FujiMultipliersPattern
}

export class FujiXTransDemosaicer extends RawDemosaicer {
	demosaicHalfSize(
		inputTexture: Texture,
		buffers: DualBuffers,
		renderer: Renderer,
		options: RawDemosaicerOptions,
	) {
		const { croppedSize, whiteBalanceMultipliers, levels, info } = options

		const modOffset: vec2 = [
			3 + options.globalOffset[0],
			(croppedSize.y % 6) + options.globalOffset[1],
		]

		// @din: We only need 3 offset to take into account the fact
		// that we do one-third preview, works for all images I tested
		// No padding is also necessary in this case
		const multipliers = this.getFujiMultipliers(info, modOffset)

		inputTexture.bind(0)
		buffers.writeBuffer.bindFramebuffer()

		renderer.backend.shaders.demosaicHalfXTransInt.render({
			source: 0,
			offset: options.globalOffset,

			redMultiplierA: multipliers.a.r,
			greenMultiplierA: multipliers.a.g,
			blueMultiplierA: multipliers.a.b,

			redMultiplierB: multipliers.b.r,
			greenMultiplierB: multipliers.b.g,
			blueMultiplierB: multipliers.b.b,

			wbCoeffs: whiteBalanceMultipliers,
			blackLevel: levels[0],
			whitePoint: levels[1],
		})
	}

	demosaicFullSize(
		inputTexture: Texture,
		buffers: DualBuffers,
		renderer: Renderer,
		options: RawDemosaicerOptions,
	) {
		const { croppedSize, whiteBalanceMultipliers, levels, cfaArray } = options

		const modOffset: vec2 = [
			options.globalOffset[0],
			(croppedSize.y % 6) + options.globalOffset[1],
		]

		const cfa = this.adjustCfaArray(cfaArray, modOffset)

		inputTexture.bind(0)
		buffers.writeBuffer.bindFramebuffer()
		renderer.backend.shaders.demosaicXTransFastInt.render({
			source: 0,
			offset: options.globalOffset,
			wbCoeffs: whiteBalanceMultipliers,
			blackLevel: levels[0],
			whitePoint: levels[1],
			cfa: cfa,
		})
	}

	private adjustCfaArray(cfaArray: number[], croppedOffset: vec2): number[] {
		const getCFAColor = (x: number, y: number) => {
			x = ((x % 6) + 6) % 6
			y = ((y % 6) + 6) % 6

			return cfaArray[x + y * 6] || 0
		}

		const cfa: number[] = new Array(36)
		for (let i = 0; i < 6; ++i) {
			for (let j = 0; j < 6; ++j) {
				const lookup = j * 6 + i

				cfa[lookup] = getCFAColor(i + croppedOffset[0], j + croppedOffset[1])
			}
		}

		return cfa
	}

	private getFujiMultipliers(info: RawInfo, cfaOffset: vec2): FujiMultipliers {
		const cfa: number[] = new Array(info.cfa_array.length)

		const getCFAColor = (info: RawInfo, x: number, y: number) => {
			x = ((x % 6) + 6) % 6
			y = ((y % 6) + 6) % 6

			return info.cfa_array[x + y * 6] || 0
		}

		for (let i = 0; i < 6; ++i) {
			for (let j = 0; j < 6; ++j) {
				const lookup = j * 6 + i

				cfa[lookup] = getCFAColor(info, i + cfaOffset[0], j + cfaOffset[1])
			}
		}

		// prettier-ignore
		const m3: mat3 = [
				0, 0, 0,
				0, 0, 0,
				0, 0, 0
			]

		const multipliers: FujiMultipliers = {
			a: {
				r: m3.slice() as mat3,
				g: m3.slice() as mat3,
				b: m3.slice() as mat3,
			},
			b: {
				r: m3.slice() as mat3,
				g: m3.slice() as mat3,
				b: m3.slice() as mat3,
			},
		}

		// Split CFA array into two repeating 3x3 chunks
		// prettier-ignore
		const patternIndicesA: Tuple<number, 9> = [
				0, 1, 2,
				6, 7, 8,
				12, 13, 14
			]

		// prettier-ignore
		const patternIndicesB: Tuple<number, 9> = [
				3, 4, 5,
				9, 10, 11,
				15, 16, 17
			]

		const computeMultipliers = (pattern: FujiMultipliersPattern, indices: number[]) => {
			const reds: number[] = []
			const greens: number[] = []
			const blues: number[] = []

			indices.forEach((cfaIndex, index) => {
				const type = cfa[cfaIndex]

				switch (type) {
					case 0: // red
						reds.push(index)
						break
					case 1: // green
						greens.push(index)
						break
					case 2: // blue
						blues.push(index)
						break
				}
			})

			reds.forEach((index) => {
				pattern.r[index] = 1 / reds.length
			})

			greens.forEach((index) => {
				pattern.g[index] = 1 / greens.length
			})

			blues.forEach((index) => {
				pattern.b[index] = 1 / blues.length
			})
		}

		computeMultipliers(multipliers.a, patternIndicesA)
		computeMultipliers(multipliers.b, patternIndicesB)

		return multipliers
	}
}
